/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { createSelector } from "reselect";
import { Machine } from "@centralwebteam/narwhal";
import { RootState } from "..";
import {
  VerdictGroup,
  JobPresentation,
  mapVerdictToGroup,
} from "@/presentation/Job";
import { JobPerformance, JobType } from "../state";
import {
  getStartDate,
  isValidStartToken,
  selectActiveFilters,
  selectAllLocations,
  selectMachinesInvariant,
  selectSearchFilterDetails,
  machineMatchesLocationOrMachineFilter,
} from "../selectors";
import { getRowId } from "@/handlers/JobPerformance/functions";

// #region internal filtering functions
const filterMachines = (
  machines: Machine[],
  locationIds: string[],
  machineIds: string[],
  machineTypes: string[],
  verdicts: VerdictGroup[],
  jobs: JobPresentation[],
  locations: { id: string; descendants: string[] }[]
) =>
  machines.filter(
    (m) =>
      machineMatchesLocationOrMachineFilter(
        m,
        { locationId: locationIds, machineId: machineIds },
        locations
      ) &&
      (!machineTypes?.length || machineTypes.includes(m.type)) &&
      (!verdicts?.length ||
        jobs.some(
          (j) => j.machineId === m.id && verdicts.includes(mapVerdictToGroup(j))
        ))
  );

const filterJobTypes = (
  jobTypes: JobType[],
  machines: Machine[],
  jobName: string[],
  verdicts: VerdictGroup[],
  jobs: JobPresentation[]
) => {
  return jobTypes.filter(
    (jt) =>
      (!jobName?.length || jobName.some((name) => jt.name === name)) &&
      machines.find((m) => jt.machines && jt.machines.includes(m.id)) &&
      (!verdicts?.length ||
        jobs.some(
          (j) => j.name === jt.name && verdicts.includes(mapVerdictToGroup(j))
        ))
  );
};
const hasFilterRequiringRecalculation = (filters: {
  locationId?: string[];
  machineId?: string[];
  machineType?: string[];
  verdict?: string[];
}) =>
  (filters.locationId?.length ?? 0) > 0 ||
  (filters.machineId?.length ?? 0) > 0 ||
  (filters.machineType?.length ?? 0) > 0 ||
  (filters.verdict?.length ?? 0) > 0;

const getFilteredJobCountsAndDurations = (
  jobPerformances: JobPerformance[],
  verdicts: VerdictGroup[]
) => {
  let counts = {} as Mappy<number>;
  let durations = {} as Mappy<number>;

  for (const jp of jobPerformances) {
    if (!verdicts?.length) {
      counts = { ...counts, [jp.name]: jp.count.total };
      durations = { ...durations, [jp.name]: jp.duration.total };
    } else {
      let count = 0,
        duration = 0;
      for (const vg of verdicts) {
        switch (vg) {
          case "Fail":
            count += jp.count.fail;
            duration += jp.duration.fail;
            break;
          case "Pass":
            count += jp.count.passAndWarning;
            duration += jp.duration.pass + jp.duration.warning;
            break;
          case "No Verdict":
            count += jp.count.noVerdict;
            duration += jp.duration.noVerdict;
            break;
        }
      }
      counts = { ...counts, [jp.name]: count };
      durations = { ...durations, [jp.name]: duration };
    }
  }

  return { filteredJobCounts: counts, filteredJobDurations: durations };
};

/** Helper to accumulate counts and durations for specific verdict groups */
const accumulateVerdictMetrics = (
  count: JobPerformance["count"],
  duration: JobPerformance["duration"],
  verdictFilter: VerdictGroup[],
  accumulator: {
    count: JobPerformance["count"];
    duration: JobPerformance["duration"];
  }
) => {
  for (const vg of verdictFilter) {
    switch (vg) {
      case "Pass":
        accumulator.count.pass += count.pass;
        accumulator.count.warning += count.warning;
        accumulator.count.passAndWarning += count.passAndWarning;
        accumulator.count.completed += count.passAndWarning;
        accumulator.count.total += count.passAndWarning;

        accumulator.duration.pass += duration.pass;
        accumulator.duration.warning += duration.warning;
        accumulator.duration.completed += duration.pass + duration.warning;
        accumulator.duration.total += duration.pass + duration.warning;
        break;
      case "Fail":
        accumulator.count.fail += count.fail;
        accumulator.count.completed += count.fail;
        accumulator.count.total += count.fail;

        accumulator.duration.fail += duration.fail;
        accumulator.duration.completed += duration.fail;
        accumulator.duration.total += duration.fail;
        break;
      case "No Verdict":
        accumulator.count.noVerdict += count.noVerdict;
        accumulator.count.incomplete += count.incomplete;
        accumulator.count.total += count.noVerdict;

        accumulator.duration.noVerdict += duration.noVerdict;
        accumulator.duration.incomplete += duration.incomplete;
        accumulator.duration.total += duration.noVerdict;
        break;
    }
  }
};

/** Calculate percentages from counts */
const calculatePercentages = (count: JobPerformance["count"]) => {
  const total = count.total;
  const totalPassFail = count.passAndWarning + count.fail;

  return {
    passAndWarning:
      totalPassFail > 0 ? (count.passAndWarning / totalPassFail) * 100 : 0,
    fail: totalPassFail > 0 ? (count.fail / totalPassFail) * 100 : 0,
    noVerdict: totalPassFail > 0 ? (count.noVerdict / totalPassFail) * 100 : 0,
    completed: total > 0 ? (count.completed / total) * 100 : 0,
    incomplete: total > 0 ? (count.incomplete / total) * 100 : 0,
  };
};

const filterJobPerformanceByVerdict = (
  performance: JobPerformance,
  verdictFilter: VerdictGroup[]
): JobPerformance => {
  if (!verdictFilter.length) return performance;

  const accumulator = {
    count: {
      fail: 0,
      warning: 0,
      pass: 0,
      passAndWarning: 0,
      noVerdict: 0,
      completed: 0,
      incomplete: 0,
      total: 0,
    },
    duration: {
      fail: 0,
      warning: 0,
      pass: 0,
      noVerdict: 0,
      completed: 0,
      incomplete: 0,
      total: 0,
    },
  };

  accumulateVerdictMetrics(
    performance.count,
    performance.duration,
    verdictFilter,
    accumulator
  );

  return {
    name: performance.name,
    cycleTime: performance.cycleTime,
    count: accumulator.count,
    duration: accumulator.duration,
    percent: calculatePercentages(accumulator.count),
  };
};
// #endregion

// #region selectors
export const selectMachinePerformances = (s: RootState) =>
  s.jobPerformance.machinePerformances;

export const selectFilteredMachinePerformances = createSelector(
  [selectMachinePerformances, selectActiveFilters],
  (machinePerformances, filters) => {
    const verdictFilter = filters.verdict as VerdictGroup[];
    if (!verdictFilter?.length) return machinePerformances;

    const filteredPerformances: MappyAbsolute<MappyAbsolute<JobPerformance>> =
      {};

    for (const [machineId, jobPerformances] of Object.entries(
      machinePerformances
    )) {
      filteredPerformances[machineId] = {};
      for (const [jobName, performance] of Object.entries(jobPerformances)) {
        filteredPerformances[machineId][jobName] =
          filterJobPerformanceByVerdict(performance, verdictFilter);
      }
    }

    return filteredPerformances;
  }
);

const selectMetadataPerformance = (s: RootState) =>
  s.jobPerformance.metadataPerformance;

export const selectFilteredMetadataPerformance = createSelector(
  [selectMetadataPerformance, selectActiveFilters],
  (metadataPerformance, filters) => {
    const verdictFilter = filters.verdict as VerdictGroup[];
    if (!verdictFilter?.length) return metadataPerformance;

    const filteredPerformances: MappyAbsolute<MappyAbsolute<JobPerformance>> =
      {};

    for (const [metadataName, jobPerformances] of Object.entries(
      metadataPerformance
    )) {
      filteredPerformances[metadataName] = {};
      for (const [jobName, performance] of Object.entries(jobPerformances)) {
        filteredPerformances[metadataName][jobName] =
          filterJobPerformanceByVerdict(performance, verdictFilter);
      }
    }

    return filteredPerformances;
  }
);

/** Selects the metadata-related parts { metadataPerformance, activeMetadata, extendedSummaryFetched } from jobPerformance state */
export const selectJobPerformanceMetadata = createSelector(
  [
    selectFilteredMetadataPerformance,
    (s: RootState) => s.jobPerformance.activeMetadata,
    (s: RootState) => s.jobPerformance.extendedSummaryFetched,
    (s: RootState) => s.jobPerformance.metadataNamesList,
  ],
  (
    metadataPerformance,
    activeMetadata,
    extendedSummaryFetched,
    metadataNamesList
  ) => ({
    metadataPerformance,
    activeMetadata,
    extendedSummaryFetched,
    metadataNamesList,
  })
);

/** Selects all sorting parameters from jobPerformance state */
export const selectJobPerformanceSorting = createSelector(
  [
    (s: RootState) => s.jobPerformance.detailSortColumn,
    (s: RootState) => s.jobPerformance.detailSortDirection,
    (s: RootState) => s.jobPerformance.machineJobAggregateSortColumn,
    (s: RootState) => s.jobPerformance.machineJobAggregateSortDirection,
    (s: RootState) => s.jobPerformance.metadataJobAggregateSortColumn,
    (s: RootState) => s.jobPerformance.metadataJobAggregateSortDirection,
    (s: RootState) => s.jobPerformance.sortColumn,
    (s: RootState) => s.jobPerformance.sortDirection,
    (s: RootState) => s.jobPerformance.filter,
  ],
  (
    detailSortColumn,
    detailSortDirection,
    machineJobAggregateSortColumn,
    machineJobAggregateSortDirection,
    metadataJobAggregateSortColumn,
    metadataJobAggregateSortDirection,
    sortColumn,
    sortDirection,
    filter
  ) => ({
    detailSortColumn,
    detailSortDirection,
    machineJobAggregateSortColumn,
    machineJobAggregateSortDirection,
    metadataJobAggregateSortColumn,
    metadataJobAggregateSortDirection,
    sortColumn,
    sortDirection,
    filter,
  })
);

/** Selects {openJobs, openMachines, openMetadataValues} from jobPerformance state */
export const selectJobPerformanceLayout = createSelector(
  [
    (s: RootState) => s.jobPerformance.openJobs,
    (s: RootState) => s.jobPerformance.openMachines,
    (s: RootState) => s.jobPerformance.openMetadataValues,
  ],
  (openJobs, openMachines, openMetadataValues) => ({
    openJobs,
    openMachines,
    openMetadataValues,
  })
);

export const selectWork = ({ jobPerformance }: RootState) =>
  jobPerformance.work;
export const selectActiveVerdictFilter = (s: RootState) =>
  s.filter.active.verdict as VerdictGroup[];

export const selectJobTypes = (s: RootState) => s.jobPerformance.jobTypes;
const selectJobSummaries = (s: RootState) => s.jobPerformance.jobSummaries;
const selectOpenJobs = (s: RootState) => s.jobPerformance.openJobs;
export const selectOpenMachines = (s: RootState) =>
  s.jobPerformance.openMachines;

export const selectFilteredJobSummaries = createSelector(
  [
    (s: RootState) => s.filter.active.verdict,
    (s: RootState) => s.jobPerformance.jobSummaries,
  ],
  (verdicts: VerdictGroup[], jobs: JobPresentation[]) => {
    if (!verdicts?.length)
      return jobs.map((j) => {
        if (
          j.type === "JobEquatorSummaryExtended" ||
          j.type === "JobMachineToolSummaryExtended" ||
          j.type === "JobCoordinateMeasurementSummaryExtended" ||
          j.type === "metrology"
        )
          return { ...j, type: "metrology" };
        else return j;
      });

    return jobs
      .filter(
        (j) =>
          (j.type === "JobEquatorSummaryExtended" ||
            j.type === "JobMachineToolSummaryExtended" ||
            j.type === "JobCoordinateMeasurementSummaryExtended" ||
            j.type === "metrology") &&
          verdicts.includes(mapVerdictToGroup(j))
      )
      .map((m) => {
        return { ...m, type: "metrology" };
      });
  }
);

export const selectFilteredMachines = createSelector(
  [
    selectMachinesInvariant,
    selectActiveFilters,
    selectJobSummaries,
    selectAllLocations,
  ],
  (machines, filters, summaries, locations) =>
    filterMachines(
      machines,
      filters.locationId,
      filters.machineId,
      filters.machineType,
      filters.verdict as VerdictGroup[],
      summaries,
      locations
    )
);

export const selectJobTypePerformances = createSelector(
  [
    (s: RootState) => s.jobPerformance.jobTypePerformances,
    selectJobTypes,
    selectMachinePerformances,
    selectFilteredMachines,
    selectActiveFilters,
  ],
  (performances, jobTypes, machinePerformances, filteredMachines, filters) => {
    if (!hasFilterRequiringRecalculation(filters)) return performances;

    const filteredMachineIds = new Set(filteredMachines.map((m) => m.id));
    const filteredPerformances: MappyAbsolute<JobPerformance> = {};
    const verdictFilter = (filters.verdict as VerdictGroup[]) || [];

    for (const jobType of jobTypes) {
      const jobName = jobType.name;
      const aggregate = {
        count: {
          fail: 0,
          warning: 0,
          pass: 0,
          passAndWarning: 0,
          noVerdict: 0,
          completed: 0,
          incomplete: 0,
          total: 0,
        },
        duration: {
          fail: 0,
          warning: 0,
          pass: 0,
          noVerdict: 0,
          completed: 0,
          incomplete: 0,
          total: 0,
        },
        cycleTimeSum: 0,
        cycleTimeCount: 0,
      };

      for (const machineId of jobType.machines) {
        if (!filteredMachineIds.has(machineId)) continue;

        const performance = machinePerformances[machineId]?.[jobName];
        if (!performance) continue;

        // Aggregate counts and durations based on verdict filter
        if (verdictFilter.length === 0) {
          // No verdict filter, include all
          aggregate.count.fail += performance.count.fail;
          aggregate.count.warning += performance.count.warning;
          aggregate.count.pass += performance.count.pass;
          aggregate.count.passAndWarning += performance.count.passAndWarning;
          aggregate.count.noVerdict += performance.count.noVerdict;
          aggregate.count.completed += performance.count.completed;
          aggregate.count.incomplete += performance.count.incomplete;
          aggregate.count.total += performance.count.total;

          aggregate.duration.fail += performance.duration.fail;
          aggregate.duration.warning += performance.duration.warning;
          aggregate.duration.pass += performance.duration.pass;
          aggregate.duration.noVerdict += performance.duration.noVerdict;
          aggregate.duration.completed += performance.duration.completed;
          aggregate.duration.incomplete += performance.duration.incomplete;
          aggregate.duration.total += performance.duration.total;
        } else {
          // With verdict filter, use helper function
          accumulateVerdictMetrics(
            performance.count,
            performance.duration,
            verdictFilter,
            aggregate
          );
        }

        if (performance.cycleTime > 0) {
          aggregate.cycleTimeSum += performance.cycleTime;
          aggregate.cycleTimeCount++;
        }
      }

      const avgCycleTime =
        aggregate.cycleTimeCount > 0
          ? aggregate.cycleTimeSum / aggregate.cycleTimeCount
          : 0;

      filteredPerformances[jobName] = {
        name: jobName,
        count: aggregate.count,
        duration: aggregate.duration,
        percent: calculatePercentages(aggregate.count),
        cycleTime: avgCycleTime,
      };
    }

    return filteredPerformances;
  }
);

export const selectFilteredJobTypes = createSelector(
  [
    selectJobTypes,
    selectFilteredMachines,
    selectActiveFilters,
    selectJobSummaries,
  ],
  (jobTypes, machines, filters, summaries) =>
    filterJobTypes(
      jobTypes,
      machines,
      filters.jobName || [""],
      filters.verdict as VerdictGroup[],
      summaries
    )
);
export const selectFilteredJobOptions = createSelector(
  [selectSearchFilterDetails, selectJobTypes],
  (FilterDetails, jobs) => {
    // Sort alphabetically by name
    const filteredJobs = [...jobs].sort((a, b) => a.name.localeCompare(b.name));

    return FilterDetails.value !== ""
      ? filteredJobs.filter((j) =>
          j.name.toLowerCase().includes(FilterDetails.value.toLowerCase())
        )
      : filteredJobs;
  }
);

export const selectJobCountsAndDurations = createSelector(
  [selectJobTypePerformances, selectActiveFilters],
  (performances, filters) =>
    getFilteredJobCountsAndDurations(
      Object.values(performances),
      filters.verdict as VerdictGroup[]
    )
);

export const selectAllExpanded = createSelector(
  [
    selectFilteredJobTypes,
    selectOpenJobs,
    selectOpenMachines,
    (s: RootState) => s.jobPerformance.openMetadataValues,
    (s: RootState) => s.jobPerformance.activeMetadata,
  ],
  (
    filteredJobTypes,
    openJobs,
    openMachines,
    openMetadataValues,
    activeMetaData
  ) => {
    const state =
      activeMetaData === "Measuring Machine"
        ? filteredJobTypes.every(
            (f) =>
              openJobs.includes(f.name) &&
              [...f.machines]
                .map((m) => getRowId(f, { id: m }))
                .every((jm) => openMachines.includes(jm))
          )
        : filteredJobTypes.every(
            (f) =>
              openJobs.includes(f.name) &&
              f.metadataNames.every((md) =>
                openMetadataValues.includes(f.name + md)
              )
          );
    return state;
  }
);

export const selectStartAndEnd = createSelector(
  [(s: RootState) => s.params],
  ({ start, end }) => {
    return [
      isValidStartToken(start) ? getStartDate(start) : new Date(start),
      end ?? new Date().toISOString(),
    ];
  }
);

export const selectLastFetchedForRefresh = (state: RootState) =>
  state.jobPerformance.lastFetchedForRefresh;

// #endregion
